import React, { useMemo } from 'react'
import { ReportElement as GqlReportElement } from 'gqlTypes'
import { Box, Typography } from '@mui/material'
import ReportElementSettings from './ReportElementSettings'
import BarChartElement from 'components/BarChartElement'
import { OptionsStackingValue, SeriesOptionsType } from 'highcharts'
import { BarChartMarginLeft } from '.'
import useReportElementChart from 'hooks/useReportElementChart'
import getBarChartData, {
  getBarChartColors,
  getBarChartHeight,
  getCohortToUse,
  getSubgroupToUse,
} from 'util/getBarChartData'
import ReportElementLegend from './ReportElementLegend'
import ReportElementFloatingLegend from './ReportElementFloatingLegend'
import ReportElementChartWithLegend from './ReportElementChartWithLegend'
import useElementCohortsAndSubgroups from 'hooks/useElementCohortsAndSubgroups'
import { ReportElementProps } from './ReportElement'

interface CustomPoint extends Highcharts.Point {
  custom?: any
}

const ReportElementStackedBarChart: React.FC<ReportElementProps> = ({
  reportId,
  element,
  report,
  sectionId,
  favorites = false,
  print = false,
  onUpdateFavorites,
}) => {
  const {
    cohort,
    subgroup,
    pastResults,
    parsedData,
    parsedContent,
    dataToggles,
    data,
    pastResultNames,
    hidden,
    handleSetHidden,
    elementTitle,
  } = useReportElementChart(element, reportId)

  // If a chart is left blank or has bad data prevent the whole page from breaking
  try {
    const [elementCohorts, elementSubgroups] = useElementCohortsAndSubgroups(
      element,
      parsedData
    )

    const cohortToUse: string = getCohortToUse(cohort || '', elementCohorts)
    const subgroupToUse: string = getSubgroupToUse(
      subgroup || '',
      elementSubgroups
    )

    const isPercent = parsedContent.data_type === 'percent'

    const chartOptions = useMemo(() => {
      const toggles = parsedContent.data_toggles
      let seriesData = getBarChartData({
        data,
        toggles,
        cohort: cohortToUse,
        subgroup: subgroupToUse,
        pastResults,
        parsedData,
      }).filter((d) => d.name != 'None')

      const initialCategories: string[] =
        dataToggles === 'Off'
          ? data.categories
          : (seriesData.map((d) => d.name).flat() as string[])
      let options = {
        title: undefined,
        colors: [],
        exporting: {
          buttons: {
            contextButton: {
              enabled: false,
            },
          },
        },
        chart: {
          height: 0,
          type: 'bar',
          marginLeft: BarChartMarginLeft,
          width: print ? 1200 : null,
        },
        xAxis: {
          categories: [...initialCategories],
          labels: {
            useHTML: true,
            style: {
              color: print ? 'black' : undefined,
              display: 'block',
              whiteSpace: 'normal', // allow wrapping in HTML
            },
            formatter: function (
              this: Highcharts.AxisLabelsFormatterContextObject
            ): string {
              // Highcharts label styles are applied to the outer container; for a fixed
              // width we wrap the value in our own block-level span.
              const value = String(this.value)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;')

              return `<span style="text-align:right;display:block;width:110px;white-space:normal;">${value}</span>`
            },
          },
        },
        yAxis: {
          min: data.y_min || 0,
          max: data.y_max || 100,
          tickInterval: data && isPercent ? 10 : data.y_interval,
          title: undefined,
          labels: {
            formatter: function (
              this: Highcharts.AxisLabelsFormatterContextObject
            ): string {
              return data && isPercent ? `${this.value}%` : `${this.value}`
            },
            style: {
              color: print ? 'black' : undefined,
            },
          },
        },
        plotOptions: {
          series: {
            animation: !print,
            stacking: 'normal' as OptionsStackingValue,
          },
          bar: {
            pointWidth: 32,
            dataLabels: {
              enabled: true,
              formatter: function (this: Highcharts.PointLabelObject): string {
                if ((this.y || 0) < 3) {
                  return ''
                }
                return data && isPercent ? `${this.y}%` : `${this.y}`
              },
              style: {
                fontSize: '14px',
                ...(print && { textOutline: 'none' }),
              },
            },
          },
        },
        legend: {
          enabled: false,
        },
        tooltip: {
          formatter: function (
            this: Highcharts.TooltipFormatterContextObject
          ): string {
            const point: CustomPoint = this.point
            let value = data && isPercent ? `${this.y}%` : this.y
            let n = point.custom
            if (n && !data.disable_hover_n_count) {
              return `<span>${this.x}</span></br><span>${this.series.name}:</span><span> ${value} </span></br><span>N = ${n}</span>`
            }
            return `<span>${this.x}</span></br><span>${this.series.name}:</span><span> ${value} </span>`
          },
        },
        credits: {
          enabled: false,
        },
        series: seriesData,
      }
      const dataLength =
        dataToggles === 'Off' ? seriesData.length : data.categories.length
      const chartColors = getBarChartColors(dataLength)
      if (dataToggles != 'Off') {
        const stackedSeries = data.categories.map((d: string, i: number) => {
          let result: SeriesOptionsType = {
            name: d,
            data: [],
            custom: [],
            type: 'bar',
            color: chartColors[i],
          }
          seriesData.forEach((n) => {
            if (n.type === 'bar' && n.data) {
              if (result.type === 'bar' && result.data) {
                result.data.push({
                  y: n.data[i] || 0,
                  color: chartColors[i],
                  custom: n.counts ? n.counts[i] : undefined,
                })
              }
            }
          })
          return result
        })
        options.series = stackedSeries.reverse()
      } else {
        const stackedSeries = seriesData.map((d, i) => {
          let result: SeriesOptionsType = {
            name: d.name,
            custom: d.counts,
            data: d.type === 'bar' ? d.data : [],
            type: 'bar',
            color: chartColors[i],
          }
          return result
        })
        options.series = stackedSeries.reverse()
      }

      const hiddenIndexes = hidden.map((h) =>
        options.xAxis.categories.findIndex((cat: string) => cat === h)
      )
      const pastResultsIndexes = pastResults
        ? []
        : pastResultNames.map((h: string) =>
            options.xAxis.categories.findIndex((cat: string) => cat === h)
          )
      options.series = options.series.map((d) => {
        if (d.type === 'bar') {
          return {
            ...d,
            data: d.data?.filter(
              (_: any, i: number) =>
                !hiddenIndexes.includes(i) && !pastResultsIndexes.includes(i)
            ),
          }
        }
        return d
      })
      options.xAxis.categories = options.xAxis.categories.filter(
        (cat: string) =>
          pastResults
            ? !hidden.includes(cat)
            : !hidden.includes(cat) && !pastResultNames.includes(cat)
      )
      options.chart.height = getBarChartHeight('stackedBarChart', options)

      return {
        barType: data?.type,
        options: options,
        initialCategories: initialCategories,
      }
    }, [parsedData, parsedContent, cohort, subgroup, pastResults, hidden])

    const showHideOptions = chartOptions.initialCategories
      .filter((item: string) =>
        pastResults ? true : !pastResultNames.includes(item)
      )
      .filter(
        (item: string) =>
          !(data.cohorts || [])
            .map((d: { name: string }) => d.name)
            .includes(item)
      )
      .map((item: string) => ({
        key: item,
        title: item,
      }))

    if (print) {
      return (
        <Box sx={{ mb: 4, width: '100%', breakInside: 'avoid' }}>
          <table style={{ width: '100%' }}>
            <thead>
              <tr style={{ breakInside: 'avoid' }}>
                <th style={{ textAlign: 'left' }}>
                  {elementTitle && (
                    <Box>
                      <Typography variant="h5">{elementTitle}</Typography>
                    </Box>
                  )}
                  <ReportElementLegend content={parsedContent} data={data} />
                  <Box mt={2}>
                    <ReportElementSettings
                      print={print}
                      reportId={reportId}
                      element={element}
                      report={report}
                      sectionId={sectionId}
                      noSubgroup={
                        dataToggles === 'Off' ||
                        dataToggles === 'Overall' ||
                        (data.segmentations && data.segmentations.length === 0)
                      }
                      noCohort={
                        dataToggles === 'Off' || dataToggles != 'Overall'
                      }
                      noPastResults={
                        dataToggles === 'Off' ||
                        dataToggles != 'Overall' ||
                        (data['past-results'] &&
                          data['past-results'].length === 0)
                      }
                      showHideOptions={showHideOptions}
                      hidden={hidden}
                      onChangeHidden={handleSetHidden}
                      favorites={favorites}
                      onUpdateFavorites={onUpdateFavorites}
                    />
                  </Box>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ breakInside: 'avoid' }}>
                <td>
                  <BarChartElement {...chartOptions} />
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr style={{ breakInside: 'avoid' }}>
                <td>
                  <ReportElementFloatingLegend
                    hidden={hidden}
                    items={chartOptions.options.series.slice().reverse()}
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </Box>
      )
    }
    return (
      <Box sx={{ mb: 4, width: '100%', breakInside: 'avoid' }}>
        {elementTitle && (
          <Box px={print ? 0 : 4}>
            <Typography variant="h5">{elementTitle}</Typography>
          </Box>
        )}
        <Box mt={2}>
          <ReportElementSettings
            print={print}
            reportId={reportId}
            element={element}
            report={report}
            sectionId={sectionId}
            noSubgroup={
              dataToggles === 'Off' ||
              dataToggles === 'Overall' ||
              (data.segmentations && data.segmentations.length === 0)
            }
            noCohort={dataToggles === 'Off' || dataToggles != 'Overall'}
            noPastResults={
              dataToggles === 'Off' ||
              dataToggles != 'Overall' ||
              (data['past-results'] && data['past-results'].length === 0)
            }
            showHideOptions={showHideOptions}
            hidden={hidden}
            onChangeHidden={handleSetHidden}
            favorites={favorites}
            onUpdateFavorites={onUpdateFavorites}
          />
        </Box>
        <ReportElementLegend content={parsedContent} data={data} />
        <ReportElementChartWithLegend
          print={print}
          legend={
            <ReportElementFloatingLegend
              hidden={hidden}
              items={chartOptions.options.series.slice().reverse()}
            />
          }
          chart={<BarChartElement {...chartOptions} />}
        />
      </Box>
    )
  } catch (error) {
    console.error(error)
    return null
  }
}

export default ReportElementStackedBarChart
